import { Injectable, Inject } from '@angular/core';
import { AuthService, UserData } from './auth.service';
import { BehaviorSubject, Observable } from 'rxjs';
const { hasAnyRole, parseRoles } = require('../../../../shared/roles.js');

export interface User {
  name: string | null;
  slack: string | null;
  role: string | null;
  id: number | null;
}

export interface LoginState {
  isLoggedIn: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private user: User = { name: null, slack: null, role: null, id: null };
  private loginState$ = new BehaviorSubject<LoginState>({ isLoggedIn: false });

  // Promise for initial loading (compatibility with current code)
  public promise: Promise<void>;

  constructor(@Inject(AuthService) private authService: AuthService) {
    // Initialize by fetching user data on startup
    this.promise = this.authService
      .getUser()
      .toPromise()
      .then((data) => {
        if (data) {
          this.setLoggedIn(data.username, data);
        }
      })
      .catch((error) => {
        // Silently handle errors (e.g., 401 when not logged in)
        // This is expected behavior when the user is not authenticated
        if (error?.status !== 401) {
          console.error('Unexpected error during login check:', error);
        }
      });
  }

  private setLoggedIn(userName: string, userData: UserData): void {
    this.user.name = userName;
    this.user.slack = userData.slack;
    this.user.role = userData.role;
    this.user.id = userData.id;
    this.loginState$.next({ isLoggedIn: true });
  }

  getLoginState$(): Observable<LoginState> {
    return this.loginState$.asObservable();
  }

  getLoginState(): LoginState {
    return this.loginState$.value;
  }

  IsLoggedIn(userName: string, userData: UserData): void {
    this.setLoggedIn(userName, userData);
  }

  IsLoggedOut(): void {
    this.user = { name: null, slack: null, role: null, id: null };
    this.loginState$.next({ isLoggedIn: false });
  }

  getUser(): User {
    return { ...this.user }; // Return copy for safety
  }

  arAdmin(): boolean {
    return parseRoles(this.user.role).includes('admin');
  }

  arChef(): boolean {
    return parseRoles(this.user.role).includes('chef');
  }

  arSalj(): boolean {
    return parseRoles(this.user.role).includes('sälj');
  }

  arStyrelse(): boolean {
    return parseRoles(this.user.role).includes('styrelse');
  }

  arPatrik(): boolean {
    return this.user.id === 1;
  }

  harNagonRoll(requiredRoles: string[] = []): boolean {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    return hasAnyRole(this.user, requiredRoles);
  }
}
